import { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import constate from "constate";
import { useAlert } from "react-alert";

import {
  FeatureGatingContext,
  SettingsContext,
  UserContext,
} from "../../contexts";
import { useCreatePxOrder } from "../../cart";
import {
  newJourney,
  newOrderJourney,
  newRequestJourney,
  journeyForRequest,
  journeyItem,
} from "../helpers/journey";
import { listingPrefill } from "../helpers/request";
import { journeyConst, journeyLabels, requestTypes } from "../constants";
import { flowVariantFor, canonicalRequestType } from "../flowVariants";
import { updateQuotes } from "../requestsApi";
import { requireResponse } from "../../core/errors/requireResponse";
import { withSlotRemoved } from "../v2/slotArrays";

export function useRequest() {
  // Surfaces the two PX failures below, which used to fail silently. Every
  // caller sits under the app-root AlertProvider; react-alert's useAlert reads
  // `.current` off its context, so a tree WITHOUT that provider throws here —
  // hence the alert provider in RequestPage.test.jsx. Calls stay optional
  // (`alert?.error?.()`) so a future bare mount degrades to silence, not a crash.
  const alert = useAlert();
  const { checkFeatureQuota } = useContext(FeatureGatingContext);
  const { isShopify, iframeQueryParams } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const location = useLocation();
  const history = useHistory();
  const { requestType: requestTypeParamRaw, requestId } = useParams(); // PX request id or request id for simple requests
  const requestTypeParamUrl = requestTypeParamRaw?.replace("?", "");
  // Purpose-built variants (record-sale / sourcing / sell-enquiry) live on the
  // same route but are NOT request types. Resolve the descriptor from the URL
  // and normalise `requestType` to the canonical order/sell value, so every
  // predicate, payload and section gate downstream is unchanged. Unknown
  // segments pass through untouched.
  const flowVariant = flowVariantFor(requestTypeParamUrl);
  const requestTypeParam = canonicalRequestType(requestTypeParamUrl);

  const { createPxOrder } = useCreatePxOrder();

  let backToRequestsOwner = false;
  if (location.search.includes("?bb=")) {
    backToRequestsOwner = location.search.split("?bb=")?.[1]?.split("&")?.[0];
  }

  // DERIVED from the URL, not duplicated into state.
  //
  // This was `useState(requestTypeParam)`, which made the address bar and this
  // value two sources of truth for one fact. They agreed at mount and nowhere
  // else: the SOURCING / SELL tab row switches flows by pushing a new URL on
  // the SAME route, so the provider never remounts and the state kept its
  // mount-time value while every derived thing around it (flowVariant, the
  // title, the lit tab) moved on. The page then read SELL ENQUIRY while this
  // said `order` — and since this is what stamps `request_type` on the draft
  // POST, a sell enquiry silently created a BUY request.
  //
  // Deriving it makes that class of bug unrepresentable, rather than papering
  // over it with an effect that syncs one copy to the other.
  //
  // Exactly two things may override the URL, and both are real:
  //   - a LOADED deal, whose type is whatever the server record says;
  //   - a PX conversion, which turns an order into a px-order mid-flow.
  // `setRequestType` sets that override; clearing it hands control back to the
  // address bar.
  const [typeOverride, setTypeOverride] = useState(null);
  const requestType = typeOverride ?? requestTypeParam ?? null;
  const setRequestType = setTypeOverride;
  // The segment the ADDRESS BAR should carry. Behaviour always keys off the
  // canonical `requestType`; this only keeps the variant in the URL so a
  // reload (or a shared link) re-enters the same purpose-built flow instead of
  // silently reverting to the stock one.
  //
  // Exception: once the rep adds a trade-in, requestType flips to px-order and
  // the URL's :requestId stops being a Request id and becomes a PxRequest id.
  // That contract is owned by savePxRequest, so hand the path back to it.
  const isPxRequestType = [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(
    requestType
  );
  const pathRequestType =
    flowVariant && !isPxRequestType ? flowVariant.urlType : requestType;
  const [listing, setListing] = useState(null);
  // The listing this flow was ENTERED with, handed over through route state by
  // “Record Sale” on a listing card or the Part Exchange button. Deliberately
  // separate from `listing` above, which is also set when an EXISTING deal is
  // hydrated (from order.items[0].listing) — those must never be treated as an
  // arrival, or the link effect below would stamp `linked_listing_id` onto a
  // quote-sourced deal and take its SELECT QUOTE section away.
  const [arrivedListing, setArrivedListing] = useState(null);
  const [listingAssignData, setListingAssignData] = useState(null);
  const [order, setOrder] = useState(null);
  const [product, setProduct] = useState(null);
  const [request, setRequest] = useState(null);
  const [mainData, setMainData] = useState(null);
  // Additional main slots (positions 1 and 2 of up to 3). Slot 0 stays
  // on the legacy singular `product` / `request` / `mainData` so the
  // huge existing single-main codepath needs no touching.
  // Indices here are mainSlotIdx-1 (i.e. additionalMainProducts[0] is
  // slot index 1 of the deal, additionalMainProducts[1] is slot 2).
  const [additionalMainProducts, setAdditionalMainProducts] = useState([]);
  const [additionalMainRequests, setAdditionalMainRequests] = useState([]);
  const [additionalMainData, setAdditionalMainData] = useState([]);
  const [pxProducts, setPxProducts] = useState([]);
  const [pxRequests, setPxRequests] = useState([]);
  const [pxData, setPxData] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [client, setClient] = useState(null);
  const [date, setDate] = useState(null);
  const [paidOffline, setPaidOffline] = useState(false);
  const [merchantClient, setMerchantClient] = useState(null);
  const [journey, setJourney] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Full-page gate for the initial hydration of an existing request.
  // True while loadInitialState/loadProducts fetches and stitches the
  // request, product, client, order, group siblings and px overlay, so
  // the 3-panel shell only reveals once every section has its data and
  // the rep can't click into a half-populated UI. A brand-new request
  // (no numeric :requestId) starts ready — there's nothing to load.
  const [isInitialLoading, setIsInitialLoading] = useState(
    () => !isNaN(parseInt(requestId))
  );
  const [selectedSendQuoteType, setSelectedSendQuoteType] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);

  // In-flight POST /api/requests promise for the legacy main slot. Used to
  // serialize draft creation: if SELECT fires more than once before the
  // first create resolves and writes request.id into state, the later calls
  // await this promise and UPDATE the same draft instead of POSTing another
  // one. Without it, each re-fire mints an orphan draft whose client_id
  // defaults to the logged-in admin (the duplicates seen in the Orders list).
  const draftCreateRef = useRef(null);

  useEffect(() => {
    if (location?.state?.reset) {
      location.state.reset = false;

      // `clearType` is still consumed (and cleared) so a stale flag cannot sit
      // in history state and fire on a later visit, but it no longer changes
      // what reset does: the type comes from the URL now, and forcing it to
      // null was a V1 behaviour for a "Choose Service" screen this route no
      // longer serves.
      if (location?.state?.clearType) {
        location.state.clearType = false;
      }

      resetState();
    }
  }, [location?.state?.reset]);

  useEffect(() => {
    loadInitialState();
  }, []);

  // The TYPE now follows the URL on its own (see the derivation above), but the
  // half-built deal underneath it does not. Switching tabs mid-pick would carry
  // an order's product and draft Request into a sell enquiry, and the next save
  // would write the sourcing product onto a sell request.
  //
  // So: drop what belongs to the ABANDONED flow, keep what does not. The client
  // is deliberately kept — they are the same person whichever way the deal is
  // written up, and making the rep re-pick them is friction with no safety
  // value. (`resetState` would have cleared them; that is why this is not just
  // a call to it.)
  //
  // Only while no request is loaded: an existing deal is reached by its own URL
  // and must never be emptied by a tab the rep happened to touch.
  const lastFlowRef = useRef(requestTypeParamUrl);
  useEffect(() => {
    const previous = lastFlowRef.current;
    lastFlowRef.current = requestTypeParamUrl;
    if (requestId) return;
    if (!previous || previous === requestTypeParamUrl) return;
    // Adding a trade-in is NOT leaving the flow: onProductSelected moves the
    // URL from /request/order/:id to an id-less /request/px-order itself (the
    // id's meaning changes to a PxRequest id). Wiping here emptied `request`,
    // so FINALIZE PART EXCHANGE silently skipped the PxRequest upsert and the
    // deal stayed a plain order + a plain sell (109654/109655, 2026-10-22).
    if (
      canonicalRequestType(previous) === requestTypes.ORDER &&
      [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(requestTypeParamUrl)
    ) {
      return;
    }

    setProduct(null);
    setRequest(null);
    setMainData(null);
    setListing(null);
    setAdditionalMainProducts([]);
    setAdditionalMainRequests([]);
    setAdditionalMainData([]);
    setPxProducts([]);
    setPxRequests([]);
    setPxData([]);
    setQuotes({});
    setJourney(newJourney());
    // A draft create still in flight belongs to the flow being left; letting the
    // next SELECT resolve to it would PUT the new product onto the old row.
    draftCreateRef.current = null;
  }, [requestTypeParamUrl, requestId]);

  // When the URL :requestId changes mid-flow (e.g., after our own
  // history.replace once a draft Request/PxRequest acquires its first
  // persisted id), re-run loadProducts so sections that hydrate off the
  // GET /api/requests/{id} or /api/px-requests/{id} response — like
  // PX SUMMARY — pick up the freshly-loaded state instead of waiting
  // for a manual page reload. Guarded with a ref so we don't double-fire
  // on the very first mount where loadInitialState already handles this.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (requestId) {
      loadProducts();
    }
    // loadProducts is stable per-render; we intentionally only re-run on
    // requestId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const loadProducts = async () => {
    if (!requestId) {
      return;
    }

    // PX-flavoured URLs may carry EITHER the Request id (newer
    // /request/order finalize → URL stays as Request id) or the
    // PxRequest id (legacy Orders-page VIEW button →
    // /request/px-order/<px_request_id>). Resolve to the Request id
    // first so the modern multi-main flow runs uniformly: try
    // /api/px-requests/{id} — if it returns a bundle, the URL was a
    // PxRequest id and we use its `data.request.id` as the effective
    // request id; otherwise treat the URL id as the Request id.
    let effectiveRequestId = requestId;
    if (
      [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(requestType)
    ) {
      try {
        const { data: pxResolve } = await axios.get(
          `/api/px-requests/${requestId}`
        );
        if (pxResolve?.request?.id) {
          effectiveRequestId = pxResolve.request.id;
        }
      } catch (_) {
        // 404 — URL id wasn't a PxRequest id; assume it's a Request
        // id and fall through to the modern /api/requests/{id} path.
      }
    }

    // Treat /request/{type}/:id as carrying the Request id for ALL
    // request-flavoured types — order, sell, px-order, px-cart. The
    // PX overlay further down (line ~170) then fetches the PxRequest
    // bundle via the Request's px_request_id. Order/sell flows
    // already worked through this branch; we just widen the gate.
    if (
      [
        requestTypes.ORDER,
        requestTypes.SELL,
        requestTypes.PX_ORDER,
        requestTypes.PX_CART,
      ].includes(requestType)
    ) {
      const { data } = await axios.get(`/api/requests/${effectiveRequestId}`);

      // A trade-in that belongs to a PX deal has ONE page: the deal's
      // px-order view (Orders / Kanban already route there). Opened at
      // /request/sell/:id, the sell loaded as the primary MAIN product with
      // its order as a second main — "Search Order Product (1/2)", both
      // watches listed as the deal's own products. Hand it to the unified
      // URL, but only when the bundle resolves to an order request: that is
      // what the px-order loader above needs to rebuild the deal.
      if (
        requestType === requestTypes.SELL &&
        data?.request_type === requestTypes.SELL &&
        data?.px_request_id
      ) {
        try {
          const { data: px } = await axios.get(
            `/api/px-requests/${data.px_request_id}`
          );
          if (px?.request?.id) {
            const isV2Url = location.pathname.startsWith("/request/v2/");
            history.replace(
              `${isV2Url ? "/request/v2" : "/request"}/${requestTypes.PX_ORDER}/${data.px_request_id}${location.search || ""}`
            );
            return;
          }
        } catch (_) {
          // Bundle unreadable — fall through and show the sell on its own.
        }
      }

      setProduct(data.product);
      setRequest(data);
      setClient(data.client);

      // Multi-main + multi-sell re-open hydration. When the loaded
      // Request belongs to a group, fetch the full group and populate
      // BOTH additional main slots (order siblings) and PX sell slots
      // (sell siblings). Opening any sibling URL — main OR sell —
      // yields the same group view; only the *primary* slot rotates
      // depending on which Request was opened.
      //
      // This is what makes a multi-request deal survive a reload:
      // every draft Request lives in DB, every sibling re-emerges on
      // the next load.
      if (data.group_id) {
        try {
          const { data: groupResp } = await axios.get(
            `/api/request-groups/${data.group_id}/requests`
          );
          const all = groupResp?.items ?? [];
          // Order siblings are extra MAIN slots, so they only hydrate under
          // an order primary. Under a sell primary they rendered the deal's
          // order as one of the sell's own products. (A PX-linked sell never
          // gets here — it is redirected to the px-order view above.)
          const orderSiblings =
            data.request_type === requestTypes.ORDER
              ? all.filter(
                  (r) =>
                    r.id !== data.id && r.request_type === requestTypes.ORDER
                )
              : [];
          // Sell siblings only hydrate when this is a finalised PX
          // deal — i.e. the primary OR at least one record in the
          // group already carries a px_request_id. Without that gate,
          // any group that ever held a sell draft (even one that was
          // never linked via FINALISE PART EXCHANGE) leaked the sell
          // into PX SUMMARY / pxRequests on a plain order view —
          // Alex hit this on staging request 5040 (group 4891, no
          // px_request_id anywhere, a stray sell appeared).
          // Also requires an ORDER primary — the sell-side URL never
          // gets the PX overlay since the sell's own flow doesn't
          // need it.
          const isOrderPrimary = data.request_type === requestTypes.ORDER;
          const groupHasPxLink =
            !!data.px_request_id ||
            all.some((r) => !!r.px_request_id);
          const sellSiblings =
            isOrderPrimary && groupHasPxLink
              ? all.filter((r) => r.request_type === requestTypes.SELL)
              : [];
          if (orderSiblings.length > 0) {
            setAdditionalMainRequests(orderSiblings);
            setAdditionalMainProducts(orderSiblings.map((r) => r.product));
          } else {
            setAdditionalMainRequests([]);
            setAdditionalMainProducts([]);
          }
          if (sellSiblings.length > 0) {
            setPxRequests(sellSiblings);
            setPxProducts(sellSiblings.map((r) => r.product));
          } else {
            // Clear stale state from any previous load — if the
            // group has no sell siblings (or the primary is itself
            // a sell), reset so PX_SUMMARY's isVisible reads zero.
            setPxRequests([]);
            setPxProducts([]);
          }
        } catch (err) {
          console.error("[RC] failed to hydrate group siblings", err);
        }
      }
      if (data.order) {
        setOrder(data.order);
        // /api/requests/:id often returns the order without the
        // items.deposits.payment relation eager-loaded, so V2's
        // useOrderManagement editingOrder hydration sees no
        // deposits and the PAYMENTS section forgets previously
        // recorded payments on refresh. Re-fetch the order via
        // /api/orders/{referenceNumber} which loads the full relation
        // tree. (The route param is the reference_number, not the id.)
        if (data.order?.reference_number) {
          // Await (rather than fire-and-forget) the full-relations order
          // so items.deposits.payment is loaded before the page reveals —
          // otherwise the PAYMENTS / Internal panel renders without its
          // payment rows and Balance Due settles only after first paint.
          try {
            const { data: fullOrder } = await axios.get(
              `/api/orders/${data.order.reference_number}`
            );
            if (fullOrder) setOrder(fullOrder);
          } catch (err) {
            console.error("[RC] failed to re-fetch full order", err);
          }
        }
      }

      // Design 2 — data-authoritative PX detection. If this is an
      // ORDER (or its PX-flavoured URL variant) whose order/request
      // is PX-linked, overlay the px-request payload so V2 has
      // sell_requests / pxProducts / quotes / listing.
      //
      // SELL requests can have px_request_id set (meaning "I'm one of
      // someone else's trade-ins"), but the SELL view should NOT
      // render the buyer's PX-summary context — it'd surface trade-in
      // sections that don't apply to a sell flow. Skip the overlay
      // for sell.
      const isOrderSideFlow =
        requestType === requestTypes.ORDER ||
        requestType === requestTypes.PX_ORDER ||
        requestType === requestTypes.PX_CART;
      const pxId = isOrderSideFlow
        ? data?.px_request_id ?? data?.order?.px_request_id ?? null
        : null;
      if (pxId) {
        try {
          const { data: px } = await axios.get(`/api/px-requests/${pxId}`);
          if (px?.order) setOrder(px.order);
          if (px?.order?.items?.[0]?.listing) {
            setListing(px.order.items[0].listing);
          }
          const sellProducts = (px?.sell_requests ?? []).map(
            (sr) => sr.product
          );
          setPxProducts(sellProducts);
          setPxRequests(px?.sell_requests ?? []);
          if (px?.selectedQuotes) setQuotes(px.selectedQuotes);
        } catch (err) {
          console.error("[RC] failed to overlay px-request data", err);
        }
      }

      return setJourney(journeyForRequest(user, requestType, [], data));
    }

    const { data } = await axios.get(`/api/px-requests/${requestId}`);
    setRequestType(data.px_request_type);
    setOrder(data.order);
    setListing(data?.order?.items?.[0]?.listing);
    setRequest(data.request);
    setProduct(
      data.request?.product || data.order?.items?.[0]?.listing?.product
    );
    const sellProducts = data.sell_requests.map((sr) => sr.product);
    setPxProducts(sellProducts);
    setPxRequests(data.sell_requests);
    setQuotes(data.selectedQuotes);
    setClient(data.request?.client || data.order?.user);

    setJourney(journeyForRequest(user, data.px_request_type, sellProducts));
  };

  const resetState = () => {
    setOrder(null);
    setListing(null);
    setProduct(null);
    setRequest(null);
    setMainData(null);
    setPxProducts([]);
    setPxRequests([]);
    setPxData([]);
    setClient(null);
    setMerchantClient(null);
    setJourney([]);
    // Drop the override so the type follows the address bar again. `clearType`
    // used to force it to null, which dates from V1's "Choose Service" screen —
    // a page that no longer exists on this route (RequestPageV2 serves bare
    // /request now). A null type on a page with no type-chooser is exactly how
    // a flow ended up with no type at all, so the flag no longer touches it.
    setTypeOverride(null);
    // Drop any in-flight/resolved create promise so the first SELECT of the
    // next deal creates a fresh draft instead of resolving to the previous
    // deal's request id and PUTting onto the wrong row.
    draftCreateRef.current = null;

    setJourney(newJourney());
  };

  // On px-order / px-cart routes the URL :requestId is the PxRequest id
  // (loadProducts uses /api/px-requests/{id} and stores the parent request
  // under `request`), NOT a Request id. Anything that GETs /api/requests/{id}
  // must therefore use request.id and must NOT fall back to the URL param on a
  // px route — doing so fires GET /api/requests/{pxId} and 404s (the SELECT
  // ORDER QUOTE save 404 on /px-order/210, and the error-boundary "reload" that
  // followed). On a non-px route the URL param IS the request id, so the
  // fallback is safe there.
  const isPxRoute = [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(
    requestTypeParamUrl
  );
  const resolvedRequestId = request?.id ?? (isPxRoute ? null : requestId);

  const refreshRequest = async () => {
    if (!resolvedRequestId) return;
    const { data } = await axios.get(`/api/requests/${resolvedRequestId}`);
    setRequest(data);
  };

  // Some backend mutations (assign-client PUT, request PUT/POST in
  // saveRequest, etc.) return a "light" Request payload that confirms the
  // change but doesn't eager-load relations like `product`, `client`, or
  // `last_quote`. If we naively `setRequest(data)` with that light payload
  // we lose the hydrated nested values from the prior load — and the smart
  // card on the right blanks out brand/model/image between mutations.
  //
  // This helper merges the new payload onto the previous one, preserving
  // any relation the new payload omits. If the new payload explicitly carries
  // a hydrated relation it wins (canonical server state); only undefined/null
  // fields fall back to the previous value.
  const setRequestPreservingRelations = (next) => {
    setRequest((prev) => {
      if (!next || !next.id) return next;
      if (!prev) return next;
      return {
        ...next,
        product: next.product || prev.product,
        client: next.client || prev.client,
        last_quote: next.last_quote || prev.last_quote,
        // created_by hydrates COMMISSIONS' Slot 1 default. Several
        // mutation endpoints return a lightweight Request without the
        // createdBy eager-load, so preserve it across updates.
        created_by: next.created_by || prev.created_by,
      };
    });
  };

  // Once a draft Request acquires its first persisted id, swap the URL
  // from /request/{type} → /request/{type}/{id} so:
  //  - reload safety: refreshing mid-flow re-hydrates the same draft
  //  - deep-linking works (rep can send the URL to a teammate)
  //  - section-state hydration paths that key off useParams.requestId
  //    fire correctly downstream (e.g. PX SUMMARY only loads when its
  //    request id is in the URL, which is why creating a new PX showed
  //    an empty PX SUMMARY until the page was reopened).
  //
  // Uses history.replace so the back button doesn't bounce through the
  // draft-URL entry, and so React Router doesn't unmount/remount the
  // page (same :requestType? :requestId? path pattern matches both
  // forms — only the params change, the component stays mounted).
  //
  // Skip px-order / px-cart routes here — their :requestId in the URL
  // is the PxRequest id, not the Request id. The PX URL update happens
  // separately when savePxRequest creates the px-request bundle.
  useEffect(() => {
    if (!request?.id || requestId) return;
    const pxRoutes = [requestTypes.PX_ORDER, requestTypes.PX_CART];
    if (pxRoutes.includes(requestType)) return;
    const isV2 = location.pathname.startsWith("/request/v2/");
    const basePath = isV2 ? "/request/v2" : "/request";
    history.replace(
      `${basePath}/${pathRequestType}/${request.id}${location.search || ""}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id, requestId, requestType]);

  const navigateStep = (journey, label, component) => {
    const item = journey.find((item) => item.component === component);
    if (item) {
      return (item.open = true);
    } else {
      journey.push(journeyItem(label, false, true, component));
    }
  };

  const loadInitialState = async () => {
    // Clear the full-page gate once every blocking fetch in this function
    // has resolved (or thrown), regardless of which branch ran — at that
    // point the 3 panels have the data they need to render coherently.
    try {
      if (location?.state?.fromRegisterOrderByAdmin) {
        setListing(location.state.listing);
        setProduct(location.state.product);
        setClient(location.state.client);
        setJourney(location.state.journey);
        setDate(location.state.date);
        setPaidOffline(!!location.state.paidOffline);

        return;
      }

      if (!isNaN(parseInt(requestId))) {
        await loadProducts();
      } else {
        if (location.state?.listing) {
          setListing(location.state?.listing);
          setArrivedListing(location.state?.listing);
          setProduct(location.state?.listing?.product);
          setJourney(newOrderJourney(user));
        } else if (location.state?.request) {
          setRequest(location.state.request);
          setClient(location.state.request?.client);
          setProduct(location.state.request?.product);
          setJourney(newRequestJourney(user));
        } else {
          resetState();
        }
      }
    } finally {
      setIsInitialLoading(false);
    }
  };

  const toggleStep = (idx) => {
    const newJourney = journey.map((step, i) => {
      if (i === idx) {
        step.open = !step.open;
      } else {
        step.open = false;
      }

      return step;
    });

    setJourney(newJourney);
  };

  const getContext = (idx, stepOverride = null) => {
    // V2 passes synthetic steps (with pxSellIdx for dynamic SELECT
    // SELL REQUEST sections, or mainSlotIdx for dynamic ADDITIONAL
    // MAIN sections) that aren't in the journey. Accept an optional
    // step override so V2 molecules can pass their own step and have
    // pxSellIdx / mainSlotIdx honored.
    const step = stepOverride ?? journey[idx];

    // V2 renders molecules without a populated journey, so fall back to
    // the main request context whenever no step matches the index.
    if (!step) {
      return {
        product,
        request,
        data: mainData,
        isEditing: request?.id,
        isSell: requestType === requestTypes.SELL,
      };
    }

    if (step.pxSellIdx !== null && step.pxSellIdx !== undefined) {
      return {
        product: pxProducts[step.pxSellIdx],
        request: pxRequests[step.pxSellIdx],
        data: pxData[step.pxSellIdx],
        isEditing: pxRequests[step.pxSellIdx]?.id,
        isSell: requestType === requestTypes.SELL || step.pxSellIdx !== null,
      };
    }

    // mainSlotIdx > 0 → additional main slot (slots 1, 2 of up to 3).
    // Slot 0 falls through to the legacy singular state path. Reads
    // from additionalMain* arrays indexed by mainSlotIdx-1.
    if (
      step.mainSlotIdx !== null &&
      step.mainSlotIdx !== undefined &&
      step.mainSlotIdx > 0
    ) {
      const arrIdx = step.mainSlotIdx - 1;
      return {
        product: additionalMainProducts[arrIdx],
        request: additionalMainRequests[arrIdx],
        data: additionalMainData[arrIdx],
        isEditing: additionalMainRequests[arrIdx]?.id,
        isSell: false,
      };
    }

    return {
      product,
      request,
      data: mainData,
      isEditing: request?.id,
      isSell: requestType === requestTypes.SELL,
    };
  };

  const onProductSelected = async (step, idx, product, url, opts = {}) => {
    // opts.prefill — when the source is "active listing" or a "paid
    // cart item", the caller passes the fields we already know
    // (year, condition, price, box, linked_listing_id, source_*)
    // so we can persist them with the draft Request and the CONFIRM
    // REQUEST form opens pre-populated.
    const prefill = opts?.prefill || {};
    // Preserve the /request/v2/... prefix when the admin is on the V2
    // page so the preview ORDER/SELL button doesn't fall back to V1.
    const isV2 = location.pathname.startsWith("/request/v2/");
    const basePath = isV2 ? "/request/v2" : "/request";
    // The URL `requestId` carries different identities depending on the
    // route:
    //   /request/order/:id      → Order Request id
    //   /request/sell/:id       → Sell Request id
    //   /request/px-order/:id   → PxRequest id (entity that joins order+sell)
    //   /request/px-cart/:id    → PxRequest id
    // When `requestType` state is flipped mid-flow (e.g. from `order` to
    // `px-order` after the rep clicks ADD), the URL param's identity is
    // about to change too. Reusing the OLD requestId verbatim against the
    // NEW path produces a URL like /request/px-order/6370 where 6370 is
    // actually an Order Request id — and the next savePxRequest then PUTs
    // /api/px-requests/6370, which is a wrong (or non-existent) PxRequest.
    // Drop the requestId when the path-type is changing; the correct
    // PxRequest id is appended later by savePxRequest's own URL replace.
    // /request/order/:id → ["request","order","6370"]   index 1 = type
    // /request/v2/order/:id → ["request","v2","order","6370"]   index 2 = type
    const urlPathParts = location.pathname.split("/").filter(Boolean);
    const urlRequestType = isV2 ? urlPathParts[2] : urlPathParts[1];
    // Compared against the URL-facing type, not the canonical one: on a
    // variant flow the path segment is e.g. "record-sale" while requestType is
    // "order", which would otherwise read as a type change on every product
    // pick and needlessly drop the requestId.
    const pathTypeIsChanging =
      !!urlRequestType && urlRequestType !== pathRequestType;
    const safeRequestId = pathTypeIsChanging ? "" : requestId || "";
    history.push(
      `${basePath}/${pathRequestType}${safeRequestId ? "/" : ""}${safeRequestId}?${iframeQueryParams}`
    );

    // For an existing request, persist the new product_id immediately
    // so downstream sections (SEND QUOTE quote list, CONNECT LISTING
    // candidates, smart card) all read the same value the user just
    // picked rather than the previous server state. pxSellIdx changes
    // are handled separately (they hit the px sell requests, not the
    // parent request) — only persist when changing the main product.
    //
    // On /request/v2/px-order/:id the URL param is the PxRequest id,
    // not the Request id — use `request.id` (the parent Order Request)
    // which is set after loadProducts hydrates the px-request.
    const targetRequestId = request?.id ?? requestId;
    // Use loose `== null` so both `null` and `undefined` are treated
    // as "main product, not a px sell slot". V2SearchProduct passes a
    // synthetic step without a pxSellIdx key (so it's undefined); the
    // previous strict `=== null` check made undefined fall into the
    // px branch and skip setProduct, breaking SELECT entirely.
    // Slot 0 (the legacy "main" slot) is identified by the absence of
    // BOTH pxSellIdx and mainSlotIdx (or mainSlotIdx === 0). Higher
    // mainSlotIdx values target the additionalMain* state arrays and
    // create their own draft Request rows joined to the same group.
    const isAdditionalMainSlot =
      step.pxSellIdx == null &&
      step.mainSlotIdx != null &&
      step.mainSlotIdx > 0;
    const isLegacyMainSlot = step.pxSellIdx == null && !isAdditionalMainSlot;

    if (isLegacyMainSlot && product?.id) {
      try {
        // Guard against the duplicate-draft race. When SELECT fires more
        // than once (double-click, or a re-emitted select event) before the
        // first POST below resolves and writes request.id into state, the
        // second invocation still sees targetRequestId as falsy and would
        // POST another draft — minting an orphan row whose client_id falls
        // back to the logged-in admin. Await any in-flight create and reuse
        // the id it produced so the re-fire UPDATES that same draft instead.
        let resolvedTargetId = targetRequestId;
        if (!resolvedTargetId && draftCreateRef.current) {
          try {
            const inflight = await draftCreateRef.current;
            resolvedTargetId = inflight?.id ?? null;
          } catch {
            // The in-flight create failed; fall through and create below.
          }
        }
        if (resolvedTargetId) {
          // Optimistic update — paint the chosen product into local state
          // before the PUT round-trip resolves so the right-hand smart card
          // updates instantly when the rep clicks SELECT on a database card.
          //
          // The `/api/products/search` payload (what V2SearchProduct hands
          // us here) is FLAT: brand_name/model_name/image_id/reference_number.
          // RequestSmartCard reads the NESTED shape produced by
          // `/api/requests/:id`: product.brand.name / product.model.name /
          // product.images[0].id. Map the flat shape to the nested shape so
          // the snapshot paints brand/model/image instantly — otherwise the
          // optimistic value blanks every field except reference_number.
          // The PUT response below then replaces this with the canonical
          // hydrated record.
          const optimisticProduct = {
            ...product,
            brand:
              product.brand ||
              (product.brand_name
                ? { id: product.brand_id, name: product.brand_name }
                : undefined),
            model:
              product.model ||
              (product.model_name
                ? { id: product.model_id, name: product.model_name }
                : undefined),
            images:
              product.images ||
              (product.image_id ? [{ id: product.image_id }] : []),
          };
          setRequest((prev) =>
            prev
              ? {
                  ...prev,
                  product: optimisticProduct,
                  product_id: product.id,
                  ...prefill,
                }
              : prev
          );
          // PUT response already returns the fresh model — use it directly
          // instead of a redundant follow-up GET. Cuts click-to-update
          // latency roughly in half.
          const { data: updated } = await axios.put(`/api/requests/${resolvedTargetId}`, {
            product_id: product.id,
            ...prefill,
          });
          if (updated && updated.id) {
            // If the PUT response is light (carries product_id but not the
            // hydrated `product` relation), keep the optimistic nested
            // product we set above — otherwise the smart card would blank
            // out brand/model/image until a subsequent GET hydrates it.
            setRequest((prev) => {
              if (!updated.product && prev?.product) {
                return { ...updated, product: prev.product };
              }
              return updated;
            });
          } else {
            await refreshRequest();
          }
        } else {
          const isSell = requestType === requestTypes.SELL;
          const isStock = requestType === requestTypes.STOCK;
          // group_id: when the rep is adding a second/third main
          // product to the same deal, the existing request belongs
          // to a group — reuse it so all main Requests share one
          // Order downstream. First-product creation lets the
          // backend mint a fresh single-member group.
          const groupId = request?.group_id ?? undefined;

          // Optimistic — same shape mapping as the PUT branch above, except
          // we synthesise a minimal `request` object since none exists yet.
          // Without this, on a brand-new /request/order flow the smart card
          // sits empty until the POST resolves; even after the POST the
          // backend's create response typically omits the hydrated product
          // relation, leaving only the flat `reference_number` visible.
          const optimisticProduct = {
            ...product,
            brand:
              product.brand ||
              (product.brand_name
                ? { id: product.brand_id, name: product.brand_name }
                : undefined),
            model:
              product.model ||
              (product.model_name
                ? { id: product.model_id, name: product.model_name }
                : undefined),
            images:
              product.images ||
              (product.image_id ? [{ id: product.image_id }] : []),
          };
          setRequest((prev) => ({
            ...(prev || {}),
            product: optimisticProduct,
            product_id: product.id,
            status: "draft",
            ...prefill,
          }));

          // Track the create promise in a ref so a concurrent re-fired
          // SELECT (see guard at the top of this branch) awaits THIS request
          // rather than starting its own. The promise resolves to the
          // created row, which carries the .id the re-fire needs to PUT.
          const createPromise = axios
            .post(`/api/requests`, {
              type: "product",
              product_id: product.id,
              request_type:
                isSell || isStock ? requestTypes.SELL : requestTypes.ORDER,
              is_stock: !!isStock,
              status: "draft",
              created_by_id: user?.id,
              client_id: client?.id || user?.id,
              ...(groupId ? { group_id: groupId } : {}),
              ...prefill,
            })
            .then((res) => res.data);
          draftCreateRef.current = createPromise;
          let newRequest;
          try {
            newRequest = await createPromise;
          } catch (postErr) {
            // Release the guard so a later SELECT can retry creation, then
            // rethrow into the outer catch (which logs the failure).
            if (draftCreateRef.current === createPromise) {
              draftCreateRef.current = null;
            }
            throw postErr;
          }
          // Follow-up GET when the POST response is light (no hydrated
          // product/brand/model/images). Same pattern as the additional-
          // main-slot branch below: RequestService::create returns the
          // minimal record; we need the eager-loaded relations so the
          // smart card paints brand/model/image, not just the reference.
          if (newRequest?.product?.brand) {
            setRequest(newRequest);
          } else if (newRequest?.id) {
            try {
              const { data: hydrated } = await axios.get(
                `/api/requests/${newRequest.id}`
              );
              setRequest(hydrated);
            } catch {
              // If the follow-up GET fails for any reason, fall back to
              // the lightweight POST response — better than leaving the
              // optimistic-only state stale.
              setRequest(newRequest);
            }
          } else {
            setRequest(newRequest);
          }
          // NB: deliberately do NOT clear draftCreateRef on success. The
          // promise stays resolved so a click arriving AFTER the POST settled
          // but BEFORE React has re-rendered the new request.id into closures
          // still awaits it here and resolves to the same id (→ PUT). Clearing
          // it now would reopen a one-frame window where a fast click sees
          // neither an id nor an in-flight create and POSTs a duplicate. The
          // ref is cleared on error (below) and in resetState (new deal); once
          // request.id is in state every SELECT short-circuits to PUT anyway.
        }
      } catch (err) {
        console.error("[RC] failed to persist product change", err);
      }
    }

    if (isAdditionalMainSlot && product?.id) {
      // Additional mains are always ORDER type and always join the
      // primary slot's group. If the legacy main hasn't been saved
      // yet (no request.id), the backend will reject the request_type
      // and group_id chain — but in practice the primary main is
      // always persisted as a draft before ADD ANOTHER PRODUCT is
      // clickable (the button lives on its CONFIRM REQUEST step).
      const arrIdx = step.mainSlotIdx - 1;
      const existing = additionalMainRequests[arrIdx];
      try {
        // Always pull the saved Request fresh after persisting so we
        // capture the eager-loaded product (brand / model / images)
        // that RequestService::create hydrates. Without this, the
        // smart card sees only the minimal {id} object from the
        // picker when the source is FROM ACTIVE LISTING and the
        // listings/search endpoint doesn't embed product fields.
        let savedRequest = null;
        if (existing?.id) {
          await axios.put(`/api/requests/${existing.id}`, {
            product_id: product.id,
            ...prefill,
          });
          const { data: refreshed } = await axios.get(
            `/api/requests/${existing.id}`
          );
          savedRequest = refreshed;
        } else {
          const groupId = request?.group_id ?? undefined;
          const { data: newRequest } = await axios.post(`/api/requests`, {
            type: "product",
            product_id: product.id,
            request_type: requestTypes.ORDER,
            status: "draft",
            created_by_id: user?.id,
            client_id: client?.id || user?.id,
            ...(groupId ? { group_id: groupId } : {}),
            ...prefill,
          });
          savedRequest = newRequest;
        }
        // Single batch: request + product. Use the saved Request's
        // product (full data) instead of the picker's raw `product`
        // which can be minimal when the source is a listing.
        setAdditionalMainRequests((arr) => {
          const next = [...arr];
          next[arrIdx] = savedRequest;
          return next;
        });
        setAdditionalMainProducts((arr) => {
          const next = [...arr];
          next[arrIdx] = savedRequest?.product || product;
          return next;
        });
      } catch (err) {
        console.error(
          "[RC] failed to persist additional main product",
          err
        );
      }
    }

    if (step.pxSellIdx != null) {
      if (pxProducts.length - 1 < step.pxSellIdx) {
        setPxProducts([...pxProducts, product || url]);
      } else {
        const newPxProducts = [...pxProducts];
        newPxProducts[step.pxSellIdx] = product || url;
        setPxProducts(newPxProducts);
      }

      // Persist a draft sell Request on every PX pick so the slot
      // survives a page reload. Without this, picking 2-3 PX sells
      // and then closing the tab loses everything (px state lived
      // only in pxProducts local React state). Drafts join the same
      // group_id as the primary main so loadProducts hydration finds
      // them on reopen. Skipped when product is a free-text URL
      // (product param can be a string for product_new flow).
      if (product?.id) {
        const arrIdx = step.pxSellIdx;
        const existing = pxRequests[arrIdx];
        const groupId = request?.group_id ?? undefined;
        // When the rep picked from the "Sell Requests" tab on a PX flow,
        // V2SearchProduct passes `prefill.source_sell_request_id` referencing
        // the *existing* standalone Sell Request the rep wants to convert
        // into the PX bundle. Previously we ignored that pointer and POST'd
        // a brand-new Sell Request, leaving the original orphaned and
        // creating a duplicate PX-SELL row each time the rep tried to
        // pick from "Sell Requests" (visible as multiple identical rows
        // for the same watch in PX SUMMARY / TRADE-INS). Use the existing
        // Sell Request id directly so finalise PART EXCHANGE links the
        // original into the px-bundle via upsertPxRequest, with no new
        // Request created.
        const reuseExistingSellRequestId =
          !existing?.id && prefill?.source_sell_request_id
            ? prefill.source_sell_request_id
            : null;
        let savedReq = null;
        try {
          if (existing?.id) {
            await axios.put(`/api/requests/${existing.id}`, {
              product_id: product.id,
              ...prefill,
            });
            const { data: refreshed } = await axios.get(
              `/api/requests/${existing.id}`
            );
            savedReq = refreshed;
          } else if (reuseExistingSellRequestId) {
            // Apply any prefill edits onto the existing Sell Request — the
            // rep may have tweaked year / condition / price_expectation in
            // the picker — then load the canonical, hydrated record so
            // pxRequests[arrIdx] carries full brand/model/images for the
            // smart card. No POST = no duplicate row.
            //
            // Strip `source_sell_request_id` from the PUT body: it's a
            // pointer that only makes sense on a *new* Request linking
            // back to its origin; PUT'ing it onto the origin record
            // itself would self-reference.
            const { source_sell_request_id: _drop, ...putBody } = prefill;
            await axios.put(
              `/api/requests/${reuseExistingSellRequestId}`,
              {
                product_id: product.id,
                ...putBody,
              }
            );
            const { data: refreshed } = await axios.get(
              `/api/requests/${reuseExistingSellRequestId}`
            );
            savedReq = refreshed;
          } else {
            const { data: newReq } = await axios.post(`/api/requests`, {
              type: "product",
              product_id: product.id,
              request_type: requestTypes.SELL,
              status: "draft",
              created_by_id: user?.id,
              client_id: client?.id || user?.id,
              ...(groupId ? { group_id: groupId } : {}),
              ...prefill,
            });
            savedReq = newReq;
          }
          if (savedReq) {
            setPxRequests((arr) => {
              const next = [...arr];
              next[arrIdx] = savedReq;
              return next;
            });
            // Replace the px products entry with the saved Request's
            // product (eager-loaded) for the smart card / display so
            // it doesn't render with minimal {id} when source was a
            // listing or existing-sell.
            if (savedReq.product) {
              setPxProducts((arr) => {
                const next = [...arr];
                next[arrIdx] = savedReq.product;
                return next;
              });
            }
          }
        } catch (err) {
          console.error(
            "[RC] failed to persist PX sell pick",
            err
          );
        }

        // Link the deal as a PX now, not only on FINALIZE PART EXCHANGE —
        // the rail lets a rep move on without that click, and every list /
        // Kanban link routes off px_request_id. FINALIZE re-runs the same
        // upsert (PUT) with the final set of trade-ins.
        if (savedReq?.id && request?.id) {
          try {
            await linkPxBundle({ idx: arrIdx, request: savedReq });
            await refreshRequest?.();
          } catch (err) {
            console.error("[RC] failed to link PX on trade-in pick", err);
            alert?.error?.(
              "The trade-in is saved but could not be linked to this order yet — FINALIZE PART EXCHANGE will retry.",
              { timeout: 10000 }
            );
          }
        }
      }

      // V2 renders without a populated journey (sections come from
      // sectionConfig instead). The product is set; section advancement
      // is owned by onAdvance in V2SearchProduct. Skip journey writes
      // when there's no journey row at this index — otherwise
      // newJourney[idx].open throws on a fresh /request/order page.
      if (!journey?.[idx]) {
        return;
      }

      const newJourney = [...journey];
      newJourney[idx].open = false;

      if (
        newJourney[idx + 1]?.component ===
        journeyConst.components.CONFIRM_REQUEST
      ) {
        newJourney[idx + 1].open = true;
        newJourney[idx + 1].hidden = false;
      } else {
        newJourney.splice(
          idx + 1,
          0,
          journeyItem(
            journeyLabels.confirmRequest,
            false,
            true,
            journeyConst.components.CONFIRM_REQUEST,
            step.pxSellIdx
          )
        );
      }

      setJourney(newJourney);
    } else {
      // setProduct controls the PRIMARY (slot 0) product in the
      // smart-card right-side panel. Additional main slots (slot 1/2)
      // own their state via additionalMainProducts[arrIdx], persisted
      // above in the isAdditionalMainSlot branch. Calling setProduct
      // here for an additional slot would overwrite the primary's
      // product in memory until the next reload, which is the snapshot
      // mis-render Alex hit when picking an Active Listings product
      // into slot 2 caused the right-side panel to show the slot-2
      // product in BOTH slot 1 and slot 2 rows.
      if (isLegacyMainSlot) {
        // Same flat→nested shape mapping as the optimistic update on
        // `request` further up — `product` from /api/products/search is
        // FLAT (brand_name/model_name/image_id), but RequestSmartCard
        // reads the NESTED shape (product.brand.name, product.images[0].id,
        // etc.) from `state.product` and falls back to state.request.product.
        // Without this mapping the smart card showed "—" for brand and no
        // image even though the request was correctly persisted.
        // If `product` is a URL string (new-product create-from-URL path),
        // pass it through verbatim — no mapping possible.
        const isNew = typeof product === "string" || !product;
        const nestedProduct = isNew
          ? product || url
          : {
              ...product,
              brand:
                product.brand ||
                (product.brand_name
                  ? { id: product.brand_id, name: product.brand_name }
                  : undefined),
              model:
                product.model ||
                (product.model_name
                  ? { id: product.model_id, name: product.model_name }
                  : undefined),
              images:
                product.images ||
                (product.image_id ? [{ id: product.image_id }] : []),
            };
        setProduct(nestedProduct);
      }

      // V2 owns section advancement via onAdvance, not via the legacy
      // journey array. On /request/order (new request, no requestId),
      // journey is empty, so newJourney[idx].open throws. Bail out
      // before touching journey when there's no row at this index.
      if (!journey?.[idx]) {
        return;
      }

      const newJourney = [...journey];
      newJourney[idx].open = false;

      if (user?.is_admin) {
        if (
          newJourney[idx + 1]?.component ===
          journeyConst.components.ASSIGN_CLIENT
        ) {
          newJourney[idx + 1].open = true;
          newJourney[idx + 1].hidden = false;
        } else {
          newJourney.splice(
            idx + 1,
            0,
            journeyItem(
              journeyLabels.assignClient,
              false,
              true,
              journeyConst.components.ASSIGN_CLIENT
            )
          );
        }
      } else {
        if (
          newJourney[idx + 1]?.component ===
          journeyConst.components.CONFIRM_REQUEST
        ) {
          newJourney[idx + 1].open = true;
          newJourney[idx + 1].hidden = false;
        } else {
          newJourney.splice(
            idx + 1,
            0,
            journeyItem(
              journeyLabels.confirmRequest,
              false,
              true,
              journeyConst.components.CONFIRM_REQUEST
            )
          );
        }
      }

      setJourney(newJourney);
    }
  };

  // ── Arrival from a listing card (Record Sale / Part Exchange) ─────────
  // Both entry points hand the listing to the flow through route state, and
  // loadInitialState sets `product` straight from it — so the rep sees the right
  // watch and everything looks fine. But nothing ever ran the Active-Listings
  // SELECT for that listing, so the Request is created with NO
  // `linked_listing_id`, and that single omission cascades: CONFIRM opens with
  // empty year / condition / price / box, PAYMENTS never appears (its isVisible
  // keys off the listing pointer), FINALISE ORDER resolves its nav target to a
  // section that does not exist, and the record-sale auto-finalise refuses to
  // run. Link it here, exactly the way picking it from the Active Listings tab
  // would.
  //
  // This lives in the PROVIDER, not in the SEARCH PRODUCT molecule, because the
  // molecule only mounts while its own section is open — and it very often is
  // not. Record Sale hoists ASSIGN CLIENT to the front and arrives with the
  // product already set, so SEARCH PRODUCT reads as complete and onAdvance
  // steps straight over it; the px-order entry point passes SEARCH PRODUCT
  // before a client exists, and never comes back. Either way an effect inside
  // that molecule never runs. The provider is mounted for the whole flow, so
  // the link cannot be skipped by where the rep happens to be standing.
  //
  // Gated on a client rather than firing on mount: linking mints the draft
  // Request, and doing that on mount would leave an orphan row (client_id
  // falling back to the logged-in admin) every time someone opened the flow and
  // backed out. Assigning a client is the rep’s first real commitment.
  const arrivalLinkedRef = useRef(false);
  useEffect(() => {
    if (arrivalLinkedRef.current) return;
    const productId =
      arrivedListing?.product_id ?? arrivedListing?.product?.id;
    if (!arrivedListing?.id || !productId) return;
    if (!client?.id) return;
    // Already pointed at a listing — a reopened request, this effect on a later
    // render, or the rep having picked one from the Active Listings tab.
    if (request?.linked_listing_id) return;
    // The rep has deliberately chosen a different watch since arriving. Their
    // choice wins; stamping the arrival listing here would silently swap the
    // product back underneath them.
    if (request?.product_id && request.product_id !== productId) return;
    arrivalLinkedRef.current = true;
    // Same synthetic PRIMARY-main step V2SearchProduct passes for slot 0, so
    // onProductSelected takes the identical branch it takes for a manual SELECT.
    Promise.resolve(
      onProductSelected(
        { id: "v2-Search Product", open: true, pxSellIdx: null, mainSlotIdx: null },
        0,
        arrivedListing.product || { id: productId },
        undefined,
        { prefill: listingPrefill(arrivedListing) }
      )
    ).catch(() => {
      // Let a later render retry rather than stranding the flow without the
      // pointer — a failed link is exactly the state this effect exists to fix.
      arrivalLinkedRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    arrivedListing?.id,
    arrivedListing?.product_id,
    client?.id,
    request?.linked_listing_id,
    request?.product_id,
  ]);

  // The assigned client is a snapshot of the row that was picked. When that
  // user is edited elsewhere (the flows' edit icon opens the Users CRM in a new
  // tab), the caller re-reads the user and hands it back here. Only `client` is
  // touched: setRequestPreservingRelations deliberately keeps the request's own
  // `client`, and loosening that merge is what this avoids.
  const refreshClient = (fresh) => {
    if (!fresh?.id) return;
    setClient((prev) => (prev ? { ...prev, ...fresh } : fresh));
  };

  const assignClient = async (step, idx, client) => {
    setClient(client);

    if (request) {
      const { data } = await axios.put(
        `/api/requests/${request.id}/update-client/${client.id}`
      );
      // Was `saveRequest(data)` — that's the full-form API caller, not
      // a state setter. The PUT already persisted the change; just
      // reflect the server's response in local state so downstream
      // sections (smart card, send quote) see the new client_id.
      // Use the relations-preserving setter so the smart card doesn't
      // blank brand/model/image when the update-client endpoint returns
      // a light payload without the eager-loaded `product` relation.
      setRequestPreservingRelations(data);
    }

    if (order) {
      const { data } = await axios.put(
        `/api/order-management/${order.id}/assign-user/${client.id}`
      );

      setOrder(data);
    }

    if (pxRequests && pxRequests.length > 0) {
      await pxRequests.forEach(async (pxRequest) => {
        const { data } = await axios.put(
          `/api/requests/${pxRequest.id}/update-client/${client.id}`
        );
      });
    }

    const newJourney = [...journey];
    newJourney[idx].open = false;

    if (
      requestType === requestTypes.PX_CART ||
      requestType === requestTypes.PX_ORDER
    ) {
      if (newJourney[idx + 3]?.component === journeyConst.components.SUMMARY) {
        newJourney[idx + 3].open = true;
        newJourney[idx + 3].hidden = false;
      } else {
        newJourney.splice(
          idx + 1,
          0,
          journeyItem(
            journeyLabels.selectToSell,
            false,
            true,
            journeyConst.components.SEARCH,
            0
          )
        );
      }
    } else {
      if (
        newJourney[idx + 2]?.component === journeyConst.components.SEND_QUOTE
      ) {
        newJourney[idx + 2].open = true;
        newJourney[idx + 2].hidden = false;
      } else {
        newJourney.splice(
          idx + 1,
          0,
          journeyItem(
            journeyLabels.confirmRequest,
            false,
            true,
            journeyConst.components.CONFIRM_REQUEST
          )
        );
      }
    }

    setJourney(newJourney);
  };

  const saveRequest = async (req, product, data, isSell, isStock = null) => {
    const params = {
      type: "product",
      created_by_id: user?.id,
      client_id: client?.id || user?.id,
      // Explicit null, never undefined: PUT /api/requests/{id} only writes the
      // keys it receives, and axios drops undefined ones — so an emptied field
      // would otherwise keep its old value instead of being cleared.
      min_budget: data.minBudget ?? null,
      max_budget: data.maxBudget ?? null,
      price_expectation: data.priceExpectation ?? null,
      year: data.year ?? null,
      condition: data.condition
        ? Array.isArray(data.condition)
          ? data.condition
          : [data.condition]
        : null,
      delivery_set: data.deliverySet ?? null,
      channel: data.channel,
      quoted_price: data.quotedPrice,
      serial_number: data.serialNumber,
      // Multi-currency: the budgets/price-expectation currency chosen in the
      // form (undefined while the flag is off → backend stamps NULL/legacy GBP).
      currency: data.currency,
      images: data.images || [],
      request_type: isSell || isStock ? requestTypes.SELL : requestTypes.ORDER,
      merchant_client_id: merchantClient?.id,
      created_at: req?.id ? undefined : date || new Date(),
      is_stock: !!isStock,
    };

    if (typeof product === "string") {
      params["type"] = "product_new";
      params["url"] = product;
    } else {
      params.product_id = product?.id;
    }

    if (!req?.id || req?.status === "draft") {
      params.status = "new";
    }

    // requireResponse: a 5xx resolves undefined (global interceptor). Throw a
    // recognisable error instead of a TypeError on `.data`.
    const { data: request } = requireResponse(
      req?.id
        ? await axios.put(`/api/requests/${req?.id}`, params)
        : await axios.post(`/api/requests`, params)
    );

    return request;
  };

  // Upsert the PxRequest that ties every trade-in to the primary order
  // Request. Runs when a trade-in is picked (so the deal is a PX the moment
  // it has one — no button order can leave it a plain order + plain sell)
  // and again on FINALIZE PART EXCHANGE. Returns the PxRequest id, or null
  // when there is no primary request / no trade-in to link yet.
  //
  // `slot` overrides one entry of the closure's pxRequests — the pick path
  // calls this before React has re-rendered the freshly saved sell into it.
  const linkPxBundle = async (slot = null) => {
    if (!request?.id) return null;
    const picked = [...(pxRequests ?? [])];
    if (slot) picked[slot.idx] = slot.request;
    // Start from the rep's in-flight picks. The group endpoint is a
    // complementary source for sells persisted into the group on a previous
    // visit, NOT a replacement: a reused existing Sell Request doesn't carry
    // this order's group_id, so the group fetch alone would drop it.
    const sellSiblings = picked.filter((sr) => sr?.id);
    if (request.group_id) {
      try {
        const { data: groupResp } = await axios.get(
          `/api/request-groups/${request.group_id}/requests`
        );
        const seen = new Set(sellSiblings.map((sr) => sr.id));
        for (const sr of groupResp?.items ?? []) {
          if (sr?.request_type === requestTypes.SELL && !seen.has(sr.id)) {
            sellSiblings.push(sr);
            seen.add(sr.id);
          }
        }
      } catch (_) {
        // Already seeded from the picks — nothing more to merge.
      }
    }
    if (sellSiblings.length === 0) return null;

    const pxPayload = {
      order_id: order?.id,
      request_id: request.id,
      sell_requests: sellSiblings.map((sr) => sr.id),
      merchant_client_id: merchantClient?.id,
    };
    const existingPxRequestId =
      request.px_request_id ?? order?.px_request_id ?? null;
    if (existingPxRequestId) {
      await axios.put(`/api/px-requests/${existingPxRequestId}`, pxPayload);
      return existingPxRequestId;
    }
    pxPayload.created_at = date || new Date();
    const { data: created } = await axios.post("/api/px-requests", pxPayload);
    return created?.id ?? null;
  };

  const checkQuota = async (step, nextAction) => {
    if (!user?.is_merchant_admin) {
      return true;
    }

    if (nextAction !== journeyConst.actions.FINISH) {
      return checkFeatureQuota(FeatureGatingContext.features.PART_EXCHANGE);
    }

    if (step.pxSellIdx != null) {
      // it is a part exchange request
      const orderAllowed = await checkFeatureQuota(
        FeatureGatingContext.features.ORDER_REQUESTS
      );
      if (!orderAllowed) {
        return false;
      }

      const sellAllowed = await checkFeatureQuota(
        FeatureGatingContext.features.SELL_REQUESTS,
        pxProducts.length
      );
      if (!sellAllowed) {
        return false;
      }
    }

    return requestType === requestTypes.SELL
      ? checkFeatureQuota(FeatureGatingContext.features.SELL_REQUESTS)
      : checkFeatureQuota(FeatureGatingContext.features.ORDER_REQUESTS);
  };

  // Any failure inside confirmRequest (a save that 5xx'd, a dropped
  // connection) used to leave `submitting` true, so the full-page
  // "Submitting request..." overlay never went away. Clear it, then rethrow
  // exactly as before so callers still do not advance.
  const confirmRequest = async (...args) => {
    try {
      return await confirmRequestUnguarded(...args);
    } catch (err) {
      setSubmitting(false);
      throw err;
    }
  };

  const confirmRequestUnguarded = async (step, idx, data, nextAction) => {
    const isNewRequest = !request;
    // Tracked across the function so we can return a target section
    // id to the caller (ConfirmRequest) for ADD / ADD_MAIN actions.
    // The journey-mutating branches set this to the id they just
    // emitted; the final return echoes it back so navigateTo doesn't
    // have to re-read stale journey state from a captured closure.
    let pendingNavigateId = null;
    setSubmitting(true);
    const allowed = await checkQuota(step, nextAction);

    if (!allowed) {
      setSubmitting(false);

      return;
    }

    // Where does this confirmRequest call's data belong?
    //   - pxSellIdx set                    → pxData[pxSellIdx]
    //   - mainSlotIdx > 0                  → additionalMainData[idx]
    //   - otherwise (legacy primary slot)  → mainData
    if (step.pxSellIdx !== null && step.pxSellIdx !== undefined) {
      setPxData((state) => {
        const newState = [...state];
        newState[step.pxSellIdx] = data;
        return newState;
      });
    } else if (
      step.mainSlotIdx !== null &&
      step.mainSlotIdx !== undefined &&
      step.mainSlotIdx > 0
    ) {
      const arrIdx = step.mainSlotIdx - 1;
      setAdditionalMainData((state) => {
        const next = [...state];
        next[arrIdx] = data;
        return next;
      });
    } else {
      setMainData(data);
    }

    // FINISH on the primary main slot persists the Request via the
    // existing saveRequest path. FINISH on an additional main slot
    // also persists, but writes back into the additionalMainRequests
    // array instead of the singular `request` state.
    const isPrimaryMain =
      (step.pxSellIdx === null || step.pxSellIdx === undefined) &&
      (step.mainSlotIdx == null || step.mainSlotIdx === 0);
    const isAdditionalMain =
      step.pxSellIdx == null &&
      step.mainSlotIdx != null &&
      step.mainSlotIdx > 0;

    // Persist the slot's Request on EVERY action transition (FINISH,
    // ADD, ADD_MAIN) — not just FINISH. Without this, clicking "Add
    // another product" or "Add part exchange" buffered the current
    // form values in local mainData/pxData/additionalMainData but
    // never PUT them to the backend, so closing the page mid-flow
    // lost everything typed since the last FINISH.
    //
    // PX slot saves are handled in the PX-cascade block further down
    // (saves all pxRequests together via the pxProducts.map loop).
    const isSaveTriggeringAction =
      nextAction === journeyConst.actions.FINISH ||
      nextAction === journeyConst.actions.ADD ||
      nextAction === journeyConst.actions.ADD_MAIN;

    if (isSaveTriggeringAction && isPrimaryMain) {
      const newRequest = await saveRequest(
        request,
        product,
        data,
        requestType === requestTypes.SELL,
        requestType === requestTypes.STOCK
      );

      // Preserve hydrated relations (product / client / last_quote) when
      // saveRequest's PUT/POST response is light — otherwise CONFIRM REQUEST
      // wipes the smart-card product card the user already saw populated
      // during SEARCH PRODUCT.
      setRequestPreservingRelations(newRequest);
    }

    if (isSaveTriggeringAction && isAdditionalMain) {
      const arrIdx = step.mainSlotIdx - 1;
      const slotReq = additionalMainRequests[arrIdx];
      const slotProduct = additionalMainProducts[arrIdx];
      const saved = await saveRequest(
        slotReq,
        slotProduct,
        data,
        false,
        false
      );
      setAdditionalMainRequests((arr) => {
        const next = [...arr];
        next[arrIdx] = saved;
        return next;
      });
    }

    // FINISH navigation target — explicit so we don't depend on
    // RequestSectionView's captured-`next` (which can be undefined
    // when the section being finished is the last visible one).
    // Under the linear PX order (SELECT QUOTE → PX SUMMARY → PAYMENTS),
    // BOTH "Finalise Part Exchange" (finishing the last CONFIRM SELL)
    // and "Continue without / Finalise" on a main CONFIRM advance to
    // the SELECT QUOTE block. PX SUMMARY is now reached AFTER the order
    // quote (via SendQuote.onSave), not straight from the sell confirm,
    // so it shows a complete net instead of an empty summary.
    if (nextAction === journeyConst.actions.FINISH) {
      pendingNavigateId = "send_quote";
    }

    // PX slot — save just this slot's sell Request on every action
    // so closing mid-flow keeps the year / condition / price the rep
    // typed for that specific PX product.
    if (
      isSaveTriggeringAction &&
      step.pxSellIdx !== null &&
      step.pxSellIdx !== undefined
    ) {
      const arrIdx = step.pxSellIdx;
      const slotReq = pxRequests[arrIdx];
      const slotProduct = pxProducts[arrIdx];
      if (slotReq?.id && slotProduct?.id) {
        try {
          const saved = await saveRequest(
            slotReq,
            slotProduct,
            data,
            true,
            false
          );
          setPxRequests((arr) => {
            const next = [...arr];
            next[arrIdx] = saved;
            return next;
          });
        } catch (err) {
          console.error("[RC] failed to save PX slot mid-flow", err);
          // Silent before: the year / condition / price the rep just typed for this
          // trade-in was gone and the flow carried on as if saved.
          alert?.error?.(
            "This part-exchange product could not be saved — re-enter its details before finalising.",
            { timeout: 10000 }
          );
        }
      }

      // Finalise Part Exchange: upsert the PxRequest bundle that ties
      // ALL sell Requests in the group to the primary order Request.
      // V1's upsertPxRequest helper used the URL `requestId` as the
      // PxRequest id — that worked when V1's PX flow URL was
      // /request/px-order/:px_request_id, but in V2's multi-main
      // flow the URL is /request/order/:order_request_id, so the
      // helper PUT'd to a non-existent PxRequest and 404'd. Use
      // request.px_request_id (the primary's existing PX link)
      // as the upsert id instead, falling back to POST when none
      // exists yet.
      if (nextAction === journeyConst.actions.FINISH) {
        try {
          // Never skip silently: a missing primary request here is exactly
          // how 109654/109655 ended up unlinked with nobody told.
          if (!request?.id) {
            throw new Error("FINISH reached with no primary order request");
          }
          const newPxRequestId = await linkPxBundle();
          if (newPxRequestId) {
            // Upgrade the URL to include the freshly-created PxRequest id
            // when the rep transitioned mid-flow from /request/order →
            // /request/px-order (the URL has no id at this point because
            // onProductSelected dropped the carried-over Order Request id).
            // Without this, the URL stays as a draft /request/px-order with
            // no id, the loadProducts re-fetch never fires (its effect keys
            // on requestId changes), and PX SUMMARY ends up empty. Use
            // history.replace so the back button doesn't bounce through
            // the id-less draft URL.
            if (
              !requestId &&
              newPxRequestId &&
              [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(requestType)
            ) {
              const isV2Url = location.pathname.startsWith("/request/v2/");
              const basePath = isV2Url ? "/request/v2" : "/request";
              history.replace(
                `${basePath}/${requestType}/${newPxRequestId}${location.search || ""}`
              );
            }

            // After upsert the primary Request has a px_request_id;
            // refresh so V2 picks it up and PX SUMMARY renders with
            // the bundle context. We keep the URL on /request/order/:id
            // intentionally — switching to /request/px-order/:px_id
            // mid-flow triggers a useRequest re-load against the new
            // requestId param (which becomes the PxRequest id, not
            // the order Request id), and that re-load races with the
            // navigateTo("px_summary") set, so the rep either lands
            // on an empty section or on SEARCH PRODUCT. The URL stays
            // as the order Request id; loadProducts already overlays
            // PX state when the Request carries a px_request_id (see
            // line 116 of this file). If a canonical PX URL is needed
            // for sharing / reload semantics, that's a follow-up that
            // needs an initial-section persistence mechanism (URL
            // query param or sessionStorage handoff) — not done here.
            await refreshRequest?.();
          }
        } catch (err) {
          console.error("[RC] failed to upsert PxRequest on FINISH", err);
          // This is the one that leaves orphaned sell requests: the trade-in saved,
          // the rep is moved on to the quote block, and nothing said the bundle was
          // never linked. Name the retry rather than just logging.
          alert?.error?.(
            "The part-exchange could not be linked to this order — the trade-in is saved. Re-open FINALISE PART EXCHANGE to link it before taking payment.",
            { timeout: 12000 }
          );
        }
      }
    }

    let newJourney = [...journey];
    if (newJourney[idx]) {
      newJourney[idx].open = false;
      if (
        step.pxSellIdx !== null ||
        nextAction === journeyConst.actions.ADD ||
        nextAction === journeyConst.actions.ADD_MAIN
      ) {
        newJourney[idx].hidden = true;
      }
    }

    if (
      nextAction === journeyConst.actions.ADD &&
      requestType === requestTypes.ORDER
    ) {
      setRequestType(requestTypes.PX_ORDER);
    }

    // ADD_MAIN — rep wants another MAIN order product in this deal.
    // Insert SEARCH + CONFIRM_REQUEST entries with the next
    // mainSlotIdx so sectionConfig.getDynamicMainSelectSections
    // renders new sections. Don't flip to PX_ORDER (this is a
    // multi-main, not a part-exchange).
    if (nextAction === journeyConst.actions.ADD_MAIN) {
      const existingMainSlots = newJourney
        .filter(
          (j) =>
            j?.component === journeyConst.components.SEARCH &&
            j?.mainSlotIdx != null
        )
        .map((j) => j.mainSlotIdx);
      const journeyNextMainSlot = existingMainSlots.length
        ? Math.max(...existingMainSlots) + 1
        : 1;
      // Hydrated slots (EDIT: group siblings live in state, not the journey)
      // occupy 1..n, so the next free slot must clear BOTH sources.
      const nextMainSlot = Math.max(
        journeyNextMainSlot,
        (additionalMainRequests?.length ?? 0) + 1
      );
      // "Add Listing" and "Source Product" are the SAME add_main action and
      // differ only in which tab the new slot opens on. The button passes its
      // intent as `data.__newSlotSource`; remember it on the journey entry so
      // the generated SEARCH section can hand it to V2SearchProduct.
      const newSlotSource = data?.__newSlotSource ?? null;
      newJourney.push(
        journeyItem(
          journeyLabels.search,
          false,
          true,
          journeyConst.components.SEARCH,
          null,
          null,
          nextMainSlot,
          newSlotSource
        )
      );
      newJourney.push(
        journeyItem(
          journeyLabels.confirmRequest,
          true,
          false,
          journeyConst.components.CONFIRM_REQUEST,
          null,
          null,
          nextMainSlot
        )
      );
      setJourney(newJourney);
      setSubmitting(false);
      // Return the target section id so the caller (ConfirmRequest)
      // can navigateTo it without re-reading stale journey state.
      return { nextSectionId: `select_main_${nextMainSlot}` };
    }

    if (!user && !merchantClient) {
      newJourney.push(
        journeyItem(
          journeyLabels.assignRequest,
          false,
          true,
          journeyConst.components.ASSIGN_REQUEST,
          step.pxSellIdx,
          nextAction
        )
      );
    } else if (nextAction === journeyConst.actions.ADD) {
      if (requestType === requestTypes.ORDER) {
        setRequestType(requestTypes.PX_ORDER);
      }

      let nextIdx = idx + 1;
      while (
        newJourney[nextIdx]?.component ===
          journeyConst.components.CONFIRM_REQUEST ||
        newJourney[nextIdx]?.component === journeyConst.components.SEARCH
      ) {
        nextIdx += 1;
      }

      // Compute the new PX sell slot index so callers can navigate
      // to the freshly inserted SEARCH section without re-reading
      // stale journey state.
      // Same two-source rule as nextMainSlot above: hydrated PX sells (EDIT)
      // are in pxProducts but not the journey, so clear both. From a main
      // confirm on a fresh flow this still lands on 0.
      const journeyNextPxIdx =
        step.pxSellIdx === null
          ? 0
          : journey.reduce(
              (mx, { pxSellIdx }) => Math.max(mx, pxSellIdx),
              0
            ) + 1;
      const newPxSellIdx = Math.max(journeyNextPxIdx, pxProducts.length);
      newJourney.splice(
        nextIdx,
        0,
        journeyItem(
          journeyLabels.selectToSell,
          false,
          true,
          journeyConst.components.SEARCH,
          newPxSellIdx
        )
      );
      // Tag the function-scope navigation hint so the final return
      // can echo it back to ConfirmRequest. sectionConfig emits
      // `select_sell_${pxSellIdx}` for the new PX SEARCH section.
      pendingNavigateId = `select_sell_${newPxSellIdx}`;
    } else if (pxProducts.length > 0) {
      const summaryStep = newJourney.find(
        ({ component }) => component === journeyConst.components.SUMMARY
      );
      if (summaryStep) {
        summaryStep.open = true;
        summaryStep.hidden = false;
      } else {
        // Keep only products that have been confirmed.
        // Fixes bug when editing prev steps and then finishing flow.
        const validPxItems = Math.max(pxData.length, step.pxSellIdx + 1);
        setPxProducts(pxProducts.slice(0, validPxItems));

        // Remove steps that have not been confirmed.
        newJourney = newJourney.filter((step) => {
          if (step.pxSellIdx !== null && step.pxSellIdx >= validPxItems) {
            return false;
          }

          return true;
        });

        newJourney.push(
          journeyItem(
            journeyLabels.summary,
            false,
            true,
            journeyConst.components.SUMMARY
          )
        );
      }
    } else {
      if (user?.is_admin) {
        if (requestType === requestTypes.STOCK) {
          navigateStep(
            newJourney,
            journeyLabels.verifySerial,
            journeyConst.components.VERIFY_SERIAL
          );
        } else {
          if (isNewRequest) {
            navigateStep(newJourney, "", journeyConst.components.SUCCESS);
          } else {
            navigateStep(
              newJourney,
              journeyLabels.sendQuote,
              journeyConst.components.SEND_QUOTE
            );
          }
        }
      } else {
        navigateStep(newJourney, "", journeyConst.components.SUCCESS);
      }
    }

    setSubmitting(false);

    setJourney(newJourney);

    // Hand back a navigation hint for ADD / ADD_MAIN callers. The
    // ADD_MAIN branch returned early with this; the ADD branch sets
    // it inline. FINISH leaves it null and the caller falls back to
    // its usual onAdvance path.
    return pendingNavigateId ? { nextSectionId: pendingNavigateId } : undefined;
  };

  const afterSendQuote = async (type) => {
    setSelectedSendQuoteType(type);
    // request.id, never the raw URL param — on a px-order URL that param is the
    // PxRequest id and this GET 404s (item: SELECT ORDER QUOTE save on
    // /px-order/210). resolvedRequestId encodes that rule.
    if (resolvedRequestId) {
      const { data } = await axios.get(`/api/requests/${resolvedRequestId}`);
      setRequest(data);
    }

    const newJourney = [...journey];
    const sendQuoteStep = newJourney.find(
      ({ component }) => component === journeyConst.components.SEND_QUOTE
    );
    if (sendQuoteStep) {
      sendQuoteStep.open = false;
      sendQuoteStep.hidden = false;
    }
    if (requestType === requestTypes.SELL) {
      navigateStep(
        newJourney,
        journeyLabels.verifySerial,
        journeyConst.components.VERIFY_SERIAL
      );
    } else if (requestType !== requestTypes.ORDER) {
      newJourney.push(
        journeyItem("", false, true, journeyConst.components.SUCCESS)
      );
    }
    setJourney(newJourney);
  };

  const onVerificationDone = (data) => {
    refreshRequest();
    const newJourney = [...journey];
    const verifySerialStep = newJourney.find(
      ({ component }) => component === journeyConst.components.VERIFY_SERIAL
    );
    if (verifySerialStep) {
      verifySerialStep.open = false;
      verifySerialStep.hidden = false;
    }

    if (requestType === requestTypes.STOCK) {
      navigateStep(
        newJourney,
        journeyLabels.payments,
        journeyConst.components.PAYMENTS
      );
      setListingAssignData(data);
    } else {
      if (
        selectedSendQuoteType == "SP_CONSIGN" ||
        selectedSendQuoteType == "CONSIGN"
      ) {
        navigateStep(
          newJourney,
          journeyLabels.consignmentAgreement,
          journeyConst.components.CONSIGNMENT_AGREEMENT
        );
      } else if (selectedSendQuoteType == "SP") {
        navigateStep(
          newJourney,
          journeyLabels.purchasingAgreement,
          journeyConst.components.PURCHASING_AGREEMENT
        );
      }
    }

    setJourney(newJourney);
  };

  const assignRequest = async (step, idx, newMerchantClient) => {
    const { request } = getContext(idx);

    if (isShopify && !user?.is_admin && newMerchantClient) {
      setMerchantClient(newMerchantClient);
    }

    if (
      request?.id &&
      [requestTypes.ORDER, requestTypes.SELL].includes(requestType)
    ) {
      if (!isShopify) {
        // Called only for simple order or sell requests
        // It knows the logged in user and that's how it assigns the request
        await axios.put(`/api/requests/${request.id}/assign-user`);
      } else if (newMerchantClient) {
        await axios.put(
          `/api/requests/${request.id}/assign-merchant-client/${newMerchantClient.id}`
        );
      }
    }

    const newJourney = [...journey];
    newJourney.pop(); // Remove assign request step

    if (step.nextAction === journeyConst.actions.FINISH) {
      setLastEvent("continue");
      if (pxProducts.length > 0) {
        newJourney.push(
          journeyItem(
            journeyLabels.summary,
            false,
            true,
            journeyConst.components.SUMMARY
          )
        );
      } else {
        newJourney.push(
          journeyItem("", false, true, journeyConst.components.SUCCESS)
        );
      }
    } else {
      // add
      newJourney.push(
        journeyItem(
          journeyLabels.selectToSell,
          false,
          true,
          journeyConst.components.SEARCH,
          step.pxSellIdx !== null ? step.pxSellIdx + 1 : 0
        )
      );
    }

    setJourney(newJourney);
  };

  const upsertPxRequest = async (newOrder, newRequest, newPxRequests) => {
    const data = {
      order_id: newOrder?.id,
      request_id: newRequest?.id,
      // Filter out null/undefined entries so an unlinked sell slot doesn't
      // crash here on req.id and so the resulting bundle doesn't carry
      // empty ids back to the backend.
      sell_requests: (newPxRequests ?? [])
        .filter((req) => req?.id)
        .map((req) => req.id),
      merchant_client_id: merchantClient?.id,
    };

    // Resolve the existing PxRequest id from any source we have. The URL
    // `requestId` is only valid as a PxRequest id when the route is
    // /request/px-order/:id or /request/px-cart/:id — on /request/order/:id
    // or /request/sell/:id, that param is the Order/Sell Request id and
    // PUTting /api/px-requests/{order_id} would target a wrong PxRequest
    // (or 404). The V2 finalize path at line ~1297 already follows the same
    // rule by only consulting `request.px_request_id`; we mirror it here so
    // both finalize paths converge on the same id and PUT rather than POST.
    const urlRequestIdIsPx = [
      requestTypes.PX_ORDER,
      requestTypes.PX_CART,
    ].includes(requestType);
    const existingPxRequestId =
      (urlRequestIdIsPx ? requestId : null) ||
      newRequest?.px_request_id ||
      request?.px_request_id ||
      newPxRequests.find((pr) => pr?.px_request_id)?.px_request_id;

    if (existingPxRequestId) {
      // Without the guard a 5xx here returned the id as if saved, and the
      // flow navigated on to the calculation page.
      requireResponse(await axios.put(`/api/px-requests/${existingPxRequestId}`, data));
      return existingPxRequestId;
    }

    data.created_at = date || new Date();

    const { data: newPxRequest } = requireResponse(await axios.post("/api/px-requests", data));

    return newPxRequest.id;
  };

  // Same as confirmRequest: never leave the submitting overlay up after a
  // failed save. Rethrows, so confirmPx does not navigate on.
  const savePxRequest = async () => {
    try {
      return await savePxRequestUnguarded();
    } catch (err) {
      setSubmitting(false);
      throw err;
    }
  };

  const savePxRequestUnguarded = async () => {
    setSubmitting(true);

    let newOrder, newRequest, newPxRequests;
    if (requestType === requestTypes.PX_CART) {
      if (!requestId) {
        newOrder = await createPxOrder({
          listing_id: listing.id,
        });
        setOrder(newOrder);
      }
    } else if (mainData) {
      newRequest = await saveRequest(
        request,
        product,
        mainData,
        requestType === requestTypes.SELL
      );
      // Preserve hydrated relations — saveRequest's PUT/POST response is
      // often light, and the PX flow's smart card needs the product nested
      // through to PX SUMMARY.
      setRequestPreservingRelations(newRequest);
    } else if (
      requestType === requestTypes.PX_ORDER &&
      !requestId &&
      // Belt-and-braces idempotency: also bail if `request` state already
      // carries a persisted id from a previous savePxRequest call in this
      // session. The URL-level `!requestId` guard handles the steady-state
      // case (after savePxRequest's history.replace sets the px-request id
      // in the URL), but there's a window between save and replace where a
      // second savePxRequest invocation — e.g. a double-click that beats
      // the disabled-button state — would re-enter this branch with the
      // URL still id-less and create a DUPLICATE Order Request. Reading
      // local `request?.id` closes that gap.
      !request?.id &&
      listing?.product
    ) {
      // Visitor came via PART EXCHANGE button on a product preview —
      // they never go through CONFIRM REQUEST so mainData is empty.
      // attachPxRequest on the backend requires either order_id or
      // request_id, so synthesize a minimal parent Order Request from
      // the listing the visitor was viewing. Admin can complete it
      // later via the V2 flow (CONNECT LISTING + SEND QUOTE).
      newRequest = await saveRequest(
        null,
        listing.product,
        {
          quotedPrice:
            listing.listing_price ?? listing.price ?? listing.sell_price,
          year: listing.year,
          condition: listing.condition ? [listing.condition] : null,
        },
        false
      );
      // Same preservation pattern — we already know the product from
      // listing.product so keep it visible if the create response is light.
      setRequestPreservingRelations(newRequest);
    }

    if ([requestTypes.PX_CART, requestTypes.PX_ORDER].includes(requestType)) {
      newPxRequests = await Promise.all(
        pxProducts.map((pxProduct, index) => {
          if (!pxData[index]) {
            return pxRequests[index];
          }

          return saveRequest(pxRequests[index], pxProduct, pxData[index], true);
        })
      );
      setPxRequests(newPxRequests);
    }

    let newPxRequestId;

    newPxRequestId = await upsertPxRequest(
      newOrder || order,
      newRequest || request,
      newPxRequests || pxRequests
    );

    // Swap the URL from /request/px-order → /request/px-order/{newPxRequestId}
    // (or px-cart) as soon as the bundle has its first persisted id, so
    // sections like PX SUMMARY that hydrate off useParams.requestId find it
    // and render content. Use replace (not push) so the back button doesn't
    // bounce through the draft-URL entry. Soft replace is safe now that the
    // PxSummary Collapsible `open` semantics are fixed for inline mode —
    // every relevant hook re-derives state from the new requestId through
    // the [requestId]-dep useEffect that re-runs loadProducts.
    if (
      newPxRequestId &&
      !requestId &&
      [requestTypes.PX_ORDER, requestTypes.PX_CART].includes(requestType)
    ) {
      const isV2 = location.pathname.startsWith("/request/v2/");
      const basePath = isV2 ? "/request/v2" : "/request";
      history.replace(
        `${basePath}/${requestType}/${newPxRequestId}${location.search || ""}`
      );
    }

    setSubmitting(false);

    return newPxRequestId || requestId;
  };

  const confirmPx = async (step, idx) => {
    if (journey.at(-1).component === journeyConst.components.SUCCESS) {
      const newJourney = [...journey];
      newJourney.pop();
      newJourney.at(-1).open = false;
      setJourney(newJourney);
    }

    const newPxRequestId = await savePxRequest();

    if (!user?.is_admin) {
      return onRegularUserConfirmation(idx);
    }

    history.push(
      `/request/${requestType}/${newPxRequestId}/calculation?${iframeQueryParams}`
    );
  };

  const onRegularUserConfirmation = (idx) => {
    if (!isShopify && user && !user?.email_verified_at) {
      history.push(`/profile?${iframeQueryParams}`);
    } else if (user) {
      history.push(
        `${order?.id ? "/orders" : "/requests/orders"}?${iframeQueryParams}`
      );
    } else {
      history.push(`/request?${iframeQueryParams}`);
      resetState();
    }
  };

  const onRemovePxRequest = async (id, pxIndex) => {
    if (id) {
      setSubmitting(true);
      await axios.put(`/api/requests/${id}/remove-from-px`);
      setSubmitting(false);
    }

    setPxProducts(pxProducts.filter((_, index) => index !== pxIndex));
    setPxRequests(pxRequests.filter((_, index) => index !== pxIndex));
    setPxData(pxData.filter((_, index) => index !== pxIndex));
  };

  /**
   * The dry-run plan for taking one slot's request out of this deal. Pure read
   * on the backend, so the confirmation dialog shows the rep what will actually
   * happen — including the reasons it will REFUSE (`blockers`).
   */
  const detachSlotPlan = async (step) => {
    const { request: slotRequest } = getContext(0, step);
    if (!slotRequest?.id) return null;

    const { data } = await axios.get(
      `/api/requests/${slotRequest.id}/detach-from-group`
    );
    return data;
  };

  /**
   * Detach one slot's request from this deal — it survives as a standalone
   * request. Hits the real endpoint, unlike om.onRemoveSellRequest, which only
   * ever mutated local state and left the deal joined on the server.
   *
   * BOTH the journey entries and the parallel slot array have to go, and the
   * remaining slots have to be RE-INDEXED: the section generators size a deal
   * by the HIGHEST slot index they can see, not by entry count, so filtering
   * alone would leave the slot count inflated and render an empty section.
   */
  const detachSlot = async (step) => {
    const { request: slotRequest } = getContext(0, step);
    if (!slotRequest?.id) return null;

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `/api/requests/${slotRequest.id}/detach-from-group`
      );

      const pxIdx = step?.pxSellIdx;
      const mainIdx = step?.mainSlotIdx;

      if (pxIdx !== null && pxIdx !== undefined) {
        setPxProducts((arr) => arr.filter((_, i) => i !== pxIdx));
        setPxRequests((arr) => arr.filter((_, i) => i !== pxIdx));
        setPxData((arr) => arr.filter((_, i) => i !== pxIdx));
        setJourney((entries) => withSlotRemoved(entries, "pxSellIdx", pxIdx));
      } else if (mainIdx !== null && mainIdx !== undefined && mainIdx > 0) {
        const arrIdx = mainIdx - 1;
        setAdditionalMainProducts((arr) => arr.filter((_, i) => i !== arrIdx));
        setAdditionalMainRequests((arr) => arr.filter((_, i) => i !== arrIdx));
        setAdditionalMainData((arr) => arr.filter((_, i) => i !== arrIdx));
        setJourney((entries) => withSlotRemoved(entries, "mainSlotIdx", mainIdx));
      }

      // px_request_id / order_id may have changed server-side (the last
      // trade-in dissolves the PX), and the OrderManagement provider remounts
      // off pxRequests, so pull the authoritative record back.
      await refreshRequest?.();

      return data;
    } finally {
      setSubmitting(false);
    }
  };

  const onConsignmentAgreementSend = () => {
    const newJourney = [...journey];
    const previousStep = newJourney.find(
      ({ component }) =>
        component === journeyConst.components.CONSIGNMENT_AGREEMENT
    );
    if (previousStep) {
      previousStep.open = false;
      previousStep.hidden = false;
    }
    if (selectedSendQuoteType == "SP_CONSIGN") {
      navigateStep(
        newJourney,
        journeyLabels.purchasingAgreement,
        journeyConst.components.PURCHASING_AGREEMENT
      );
    } else {
      navigateStep(
        newJourney,
        journeyLabels.payments,
        journeyConst.components.PAYMENTS
      );
    }
    setJourney(newJourney);
  };

  const onUploadInvoice = (files) => {
    const newJourney = [...journey];
    const previousStep = newJourney.find(
      ({ component }) => component === journeyConst.components.UPLOAD_INVOICE
    );
    if (previousStep) {
      previousStep.open = false;
      previousStep.hidden = false;
    }
    navigateStep(
      newJourney,
      journeyLabels.assignListing,
      journeyConst.components.ASSIGN_LISTING
    );

    setJourney(newJourney);
  };

  const onAssignListing = async (listing) => {
    await axios.put(`/api/listings/${listing.id}/assign`, {
      ...listing,
      price: request.price_expectation,
      serial_number: listingAssignData.serial_number,
      private_note: `Created from Sell Request DAO-S${request.id}`,
    });
    const newJourney = [...journey];
    const previousStep = newJourney.find(
      ({ component }) => component === journeyConst.components.ASSIGN_CLIENT
    );
    if (previousStep) {
      previousStep.open = false;
      previousStep.hidden = false;
    }
    newJourney.push(
      journeyItem("", false, true, journeyConst.components.SUCCESS)
    );
    setJourney(newJourney);
  };

  const onPurchaseAgreementSend = () => {
    const newJourney = [...journey];
    const previousStep = newJourney.find(
      ({ component }) =>
        component === journeyConst.components.PURCHASING_AGREEMENT
    );
    if (previousStep) {
      previousStep.open = false;
      previousStep.hidden = false;
    }
    navigateStep(
      newJourney,
      journeyLabels.payments,
      journeyConst.components.PAYMENTS
    );
    setJourney(newJourney);
  };

  const onPurchaseOrderSend = () => {
    const newJourney = [...journey];
    const previousStep = newJourney.find(
      ({ component }) => component === journeyConst.components.PURCHASE_ORDER
    );
    if (previousStep) {
      previousStep.open = false;
      previousStep.hidden = false;
    }
    navigateStep(
      newJourney,
      journeyLabels.publishListing,
      journeyConst.components.PUBLISH_LISTING
    );
    setJourney(newJourney);
  };

  const afterAddingPrice = (values) => {
    const newJourney = [...journey];
    const previousStep = newJourney.find(
      ({ component }) => component === journeyConst.components.PAYMENTS
    );
    if (previousStep) {
      previousStep.open = false;
      previousStep.hidden = false;
    }

    if (requestType === requestTypes.STOCK) {
      navigateStep(
        newJourney,
        journeyLabels.uploadInvoice,
        journeyConst.components.UPLOAD_INVOICE
      );
    } else {
      navigateStep(
        newJourney,
        journeyLabels.purchaseOrder,
        journeyConst.components.PURCHASE_ORDER
      );
    }

    setJourney(newJourney);
  };

  /**
   * Record the quote a SELECT QUOTE section picked for ONE slot.
   *
   * PX SUMMARY and the Live Calculation both read the `quotes` map keyed by
   * request id — that is the only place a sell request's chosen quote counts.
   * A SELECT QUOTE section therefore has to write here, not just into its own
   * component state, or the banner keeps showing the previous net.
   *
   * Persistence targets the PX BUNDLE: `PUT /api/px-requests/{id}/quotes`. The
   * id must be the PxRequest, and on a V2 `/request/order/:id` URL the URL param
   * is the ORDER REQUEST — PUTting that hits a different row entirely (the same
   * mistake the PxRequest upsert in confirmRequest documents). So resolve
   * px_request_id off the request/order and skip the write when there is none
   * yet; local state still updates, so the banner stays live either way.
   */
  const selectSlotQuote = async (slotRequestId, quote) => {
    if (!slotRequestId || !quote?.id) return;

    const next = { ...quotes, [slotRequestId]: quote };
    setQuotes(next);

    const pxId = request?.px_request_id ?? order?.px_request_id ?? null;
    if (!pxId) return;
    try {
      await updateQuotes(pxId, next);
    } catch (err) {
      // Non-fatal: the selection is already in state, so the rep sees the right
      // figures. Losing it costs one re-pick after a reload.
      console.error("[RC] failed to persist the slot quote selection", err);
    }
  };

  return {
    order,
    listing,
    product,
    request,
    mainData,
    // Purpose-built flow overlay (null on the stock order/sell/px flows).
    // Consumers: sectionConfig (section set + labels), V2SearchProduct
    // (default source tab), ConfirmRequest (button labels).
    flowVariant,
    pxProducts,
    pxRequests,
    setPxProducts,
    setPxRequests,
    pxData,
    additionalMainProducts,
    additionalMainRequests,
    additionalMainData,
    journey,
    requestType,
    requestId,
    client,
    paidOffline,
    toggleStep,
    getContext,
    onProductSelected,
    assignClient,
    refreshClient,
    confirmRequest,
    assignRequest,
    confirmPx,
    updateQuotes: (quotes) => updateQuotes(requestId, quotes),
    setQuotes,
    selectSlotQuote,
    afterSendQuote,
    onVerificationDone,
    onConsignmentAgreementSend,
    onPurchaseAgreementSend,
    afterAddingPrice,
    onUploadInvoice,
    onAssignListing,
    setRequest,
    // Exposed so consumers (e.g. SendQuote's fallback when no slot
    // applies) can apply the same hydrated-relation preservation as
    // the internal mutation paths and avoid blanking smart-card fields
    // when a quote-save response is light.
    setRequestPreservingRelations,
    // slotSetRequest routes a request update into the right state
    // slot based on the synthetic step. Used by SendQuote.onQuoteSaved
    // so saving a quote on slot 2 of a multi-main deal writes back
    // into additionalMainRequests[1] instead of clobbering the
    // singular primary `request` state.
    slotSetRequest: (step, nextReq) => {
      if (!step) {
        setRequestPreservingRelations(nextReq);
        return;
      }
      if (step.pxSellIdx !== null && step.pxSellIdx !== undefined) {
        setPxRequests((arr) => {
          const next = [...arr];
          next[step.pxSellIdx] = nextReq;
          return next;
        });
        return;
      }
      if (
        step.mainSlotIdx !== null &&
        step.mainSlotIdx !== undefined &&
        step.mainSlotIdx > 0
      ) {
        const arrIdx = step.mainSlotIdx - 1;
        setAdditionalMainRequests((arr) => {
          const next = [...arr];
          next[arrIdx] = nextReq;
          return next;
        });
        return;
      }
      setRequestPreservingRelations(nextReq);
    },
    refreshRequest,
    onPurchaseOrderSend,
    quotes,
    onRemovePxRequest: pxProducts.length > 1 ? onRemovePxRequest : null,
    detachSlotPlan,
    detachSlot,
    submitting,
    isInitialLoading,
    backToRequestsOwner,
    lastEvent,
    setLastEvent,
  };
}

const [RequestProvider, useRequestContext] = constate(useRequest);

export { RequestProvider, useRequestContext };
